// client/src/pages/ProductDetails/ProductDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import "./ProductDetails.css";
import API_BASE_URL from "../../apiConfig";
import IMG_HOST from "../../apiConfigImgs";

const ProductDetails = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [checkoutHistoryOpen, setCheckoutHistoryOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/inventory/${sku}`, {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Error fetching product details");
        setLoading(false);
      });
  }, [sku]);

  const handlePrevImage = () => {
    if (product?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (product?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
    }
  };

  const handleEdit = () => navigate(`/edit-product/${sku}`);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/${sku}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Error deleting product");
      navigate("/inventory");
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getImageUrl = (imgUrl) => {
    if (!imgUrl) return `${process.env.PUBLIC_URL}/static/imgs/fallback-image.png`;
    return imgUrl.startsWith('/uploads/')
      ? `${IMG_HOST}${imgUrl.replace('/uploads/', '')}`
      : imgUrl.startsWith('http')
      ? imgUrl
      : `${IMG_HOST}${imgUrl}`;
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!product) return <div className="error">No product found.</div>;

  const stockStatus = product.quantity > 0 ? "In Stock" : "Out of Stock";
  const formattedLastUpdated = new Date(product.lastUpdated).toLocaleString();

  return (
    <div className="product-details-page">
      <div className="container">
        <div className="product-details-card">
          <div className="image-section">
            {product.images?.length > 0 ? (
              <div className="image-slider">
                <button className="slider-btn prev" onClick={handlePrevImage}>
                  ‹
                </button>
                <img
                  src={getImageUrl(product.images[currentImageIndex].url)}
                  alt={product.images[currentImageIndex].description || product.itemName}
                  className="product-img"
                  onError={(e) => (e.target.src = `${process.env.PUBLIC_URL}/static/imgs/fallback-image.png`)}
                />
                <button className="slider-btn next" onClick={handleNextImage}>
                  ›
                </button>
              </div>
            ) : (
              <div className="no-image">No Image Available</div>
            )}
          </div>

          <div className="info-section">
            <h1 className="product-title">{product.itemName}</h1>
            <p><strong>SKU:</strong> {product.sku}</p>
            <p>
              <strong>Quantity:</strong> {product.quantity}{" "}
              <span className={`stock-status ${product.quantity > 0 ? "in-stock" : "out-stock"}`}>
                {stockStatus}
              </span>
            </p>
            <p><strong>Location:</strong> {product.location}</p>
            <div className="qr-section">
              <p><strong>QR Code:</strong></p>
              <QRCodeCanvas
                value={product.qrCode}
                size={128}
                bgColor="#ffffff"
                fgColor="#522398"
                level="H"
                includeMargin={true}
              />
            </div>
            {product.checkedOutBy?.name && (
              <p><strong>Checked Out By:</strong> {product.checkedOutBy.name}</p>
            )}
            <p><strong>Created By:</strong> {product.createdBy?.name || "Unknown"}</p>
            <p><strong>Last Updated:</strong> {formattedLastUpdated}</p>
            <div className="actions">
              <button className="action-btn checkout">Check Out</button>
              <div className="admin-actions">
                <button className="action-btn edit" onClick={handleEdit}>Edit</button>
                <button className="action-btn delete" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>

        <div className="history-section">
          <button
            className="toggle-btn"
            onClick={() => setCheckoutHistoryOpen(!checkoutHistoryOpen)}
          >
            {checkoutHistoryOpen ? "Hide" : "Show"} History
          </button>
          {checkoutHistoryOpen && product.checkoutHistory?.length > 0 && (
            <div className="history-list">
              {product.checkoutHistory.map((entry, index) => (
                <div key={index} className="history-entry">
                  <p><strong>User:</strong> {entry.user?.name || "Unknown"}</p>
                  <p><strong>Checked Out:</strong> {new Date(entry.checkedOutAt).toLocaleString()}</p>
                  {entry.returnedAt && (
                    <p><strong>Returned:</strong> {new Date(entry.returnedAt).toLocaleString()}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;